import {
  MODBUS_FUNC_READ_COILS, MODBUS_FUNC_READ_DISCRETE_INPUTS,
  MODBUS_FUNC_READ_INPUT_REGISTERS,
  MODBUS_FUNC_READ_MULTIPLE_HOLDING_REGISTERS, MODBUS_FUNC_WRITE_SINGLE_COIL,
  MODBUS_FUNC_WRITE_SINGLE_HOLDING_REGISTER,
  ModbusFunctionType,
} from './modbus-functions';
import {Tile, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL, TILE_TYPE_SIMPLE_MULTISTATE, TileType} from './tile';
import {TileIcon} from './tile-icon';
import {TILE_APPEARANCE_DEFAULT_DECIMAL, TILE_APPEARANCE_DEFAULT_INCREMENT, TILE_APPEARANCE_DEFAULT_MULTIPLIER} from './appearance';
import {TileStructure} from './structure';
import {DeviceId, DeviceType} from './device';

export interface ModbusConfig {
  Slave: number;
  Points: ModbusPoint[];
}

export interface ModbusPoint {
  ID: string; // serialized form of ModbusExtractedID
  Name: string;
}

export enum ModbusDataType {
  Bool = 'bool',
  Float32 = 'float32',
  Float64 = 'float64',
  Int16 = 'int16',
  Int32 = 'int32',
  Int64 = 'int64',
  Uint16 = 'uint16',
  Uint32 = 'uint32',
  Uint64 = 'uint64',
}

export enum ModbusEndianness {
  Empty = '',
  BigEndian = 'Aa',
  LittleEndian = 'aA',
  BigEndian32 = 'AaBb',
  LittleEndian32 = 'aAbB',
  BigEndian32Swapped = 'BbAa',
  LittleEndian32Swapped = 'bBaA',
  BigEndian64 = 'AaBbCcDd',
  LittleEndian64 = 'aAbBcCdD',
  BigEndian64Swapped = 'DdCcBbAa',
  LittleEndian64Swapped = 'dDcCbBaA'
}

// Modbus function IDs matching the protocol specification
export enum ModbusFunctionId {
  READ_DISCRETE_INPUTS = 'read-2',
  READ_WRITE_COILS = 'read-1-write-5',
  READ_INPUT_REGISTERS = 'read-4',
  READ_WRITE_HOLDING = 'read-3-write-6',
}


export class ModbusPointID {
  Type: ModbusDataType = ModbusDataType.Int16;
  Address: number = 0;
  Endianness: ModbusEndianness = ModbusEndianness.BigEndian;
  Writable: boolean = false;


  constructor(str?: string) {
    if (str) {
      this.deserialize(str);
    }
  }


  serialize(): string {
    return JSON.stringify([
      this.Type,
      this.Address,
      this.Endianness,
      this.Writable,
    ]);
  }


  deserialize(str: string) {
    const obj = JSON.parse(str);

    this.Type = obj[0];
    this.Address = obj[1];
    this.Endianness = obj[2];
    this.Writable = obj[3];
  }


  getFriendlyTypeString(): string {
    return getFriendlyTypeString(this.Type, this.Writable);
  }


  getFriendlyEndiannessString(): string {
    return getFriendlyEndiannessString(this.Endianness);
  }


  getFriendlyFunctionsString(): string {
    if (this.Writable) {
      return makeFriendlyFunctionsString(getReadWriteFunctionsForDataType(this.Type));
    } else {
      return makeFriendlyFunctionsString(getReadOnlyFunctionsForDataType(this.Type));
    }
  }
}


export function makeFriendlyFunctionsString(funcs: ModbusFunctionType[]): string {
  return funcs.map(c => `${c.functionWrites ? 'Write' : 'Read'} (${c.code})`).join(' / ');
}


/**
 * Returns the valid endianness values for a given data type.
 */
export function getValidEndiannessForDataType(dataType: ModbusDataType): ModbusEndianness[] {
  switch (dataType) {
    case ModbusDataType.Bool:
      return [ModbusEndianness.Empty];
    case ModbusDataType.Int16:
    case ModbusDataType.Uint16:
      return [ModbusEndianness.BigEndian, ModbusEndianness.LittleEndian];
    case ModbusDataType.Int32:
    case ModbusDataType.Uint32:
    case ModbusDataType.Float32:
      return [
        ModbusEndianness.BigEndian32,
        ModbusEndianness.LittleEndian32,
        ModbusEndianness.BigEndian32Swapped,
        ModbusEndianness.LittleEndian32Swapped,
      ];
    case ModbusDataType.Int64:
    case ModbusDataType.Uint64:
    case ModbusDataType.Float64:
      return [
        ModbusEndianness.BigEndian64,
        ModbusEndianness.LittleEndian64,
        ModbusEndianness.BigEndian64Swapped,
        ModbusEndianness.LittleEndian64Swapped,
      ];
    default:
      throw new Error('unsupported Modbus data type');
  }
}


/**
 * Returns the default endianness for a given data type.
 * Used for auto-correction when an incompatible endianness is selected.
 */
export function getDefaultEndiannessForDataType(dataType: ModbusDataType): ModbusEndianness {
  switch (dataType) {
    case ModbusDataType.Bool:
      return ModbusEndianness.Empty;
    case ModbusDataType.Int16:
    case ModbusDataType.Uint16:
      return ModbusEndianness.BigEndian;
    case ModbusDataType.Int32:
    case ModbusDataType.Uint32:
    case ModbusDataType.Float32:
      return ModbusEndianness.BigEndian32;
    case ModbusDataType.Int64:
    case ModbusDataType.Uint64:
    case ModbusDataType.Float64:
      return ModbusEndianness.BigEndian64;
    default:
      throw new Error('unsupported Modbus data type');
  }
}


/**
 * Checks if an endianness value is valid for a given data type.
 * If not, returns a corrected endianness value.
 */
export function correctEndiannessForDataType(
  dataType: ModbusDataType,
  endianness: ModbusEndianness
): ModbusEndianness {
  const validEndianness = getValidEndiannessForDataType(dataType);

  if (validEndianness.includes(endianness)) {
    return endianness;
  }

  // If the selected endianness is not valid, return the default
  return getDefaultEndiannessForDataType(dataType);
}


/**
 * Returns the Modbus functions that are possible for a given point, when
 * it is known that the point is writable and the ModbusDataType is the
 * supplied value.
 *
 * @param dataType
 */
export function getReadWriteFunctionsForDataType(dataType: ModbusDataType): ModbusFunctionType[] {
  const funcs: ModbusFunctionType[] = [];

  switch (dataType) {
    case ModbusDataType.Bool: {
      funcs.push(MODBUS_FUNC_READ_COILS, MODBUS_FUNC_WRITE_SINGLE_COIL);
      break;
    }
    case ModbusDataType.Float32:
    case ModbusDataType.Float64:
    case ModbusDataType.Int16:
    case ModbusDataType.Int32:
    case ModbusDataType.Int64:
    case ModbusDataType.Uint16:
    case ModbusDataType.Uint32:
    case ModbusDataType.Uint64: {
      funcs.push(MODBUS_FUNC_READ_MULTIPLE_HOLDING_REGISTERS, MODBUS_FUNC_WRITE_SINGLE_HOLDING_REGISTER);
      break;
    }
    default: {
      throw new Error('unsupported Modbus data type');
    }
  }

  return funcs;
}


/**
 * Returns the Modbus functions that are possible for a given point, when
 * it is known that the point is read-only and the ModbusDataType is the
 * supplied value.
 *
 * @param dataType
 */
export function getReadOnlyFunctionsForDataType(dataType: ModbusDataType): ModbusFunctionType[] {
  const funcs: ModbusFunctionType[] = [];

  switch (dataType) {
    case ModbusDataType.Bool: {
      funcs.push(MODBUS_FUNC_READ_DISCRETE_INPUTS);
      break;
    }
    case ModbusDataType.Float32:
    case ModbusDataType.Float64:
    case ModbusDataType.Int16:
    case ModbusDataType.Int32:
    case ModbusDataType.Int64:
    case ModbusDataType.Uint16:
    case ModbusDataType.Uint32:
    case ModbusDataType.Uint64: {
      funcs.push(MODBUS_FUNC_READ_INPUT_REGISTERS);
      break;
    }
    default: {
      throw new Error('unsupported Modbus data type');
    }
  }

  return funcs;
}


export function getFriendlyTypeString(dataType: ModbusDataType, writable: boolean): string {
  switch (dataType) {
    case ModbusDataType.Bool: {
      return writable ? '1-bit Boolean (Output Coil)' : '1-bit Boolean (Input Contact)';
    }
    case ModbusDataType.Float32: {
      return writable ? '32-bit Float (Holding Register)' : '32-bit Float (Input Register)';
    }
    case ModbusDataType.Float64: {
      return writable ? '64-bit Float (Holding Register)' : '64-bit Float (Input Register)';
    }
    case ModbusDataType.Int16: {
      return writable ? '16-bit Integer, Signed (Holding Register)' : '16-bit Integer, Signed (Input Register)';
    }
    case ModbusDataType.Int32: {
      return writable ? '32-bit Integer, Signed (Holding Register)' : '32-bit Integer, Signed (Input Register)';
    }
    case ModbusDataType.Int64: {
      return writable ? '64-bit Integer, Signed (Holding Register)' : '64-bit Integer, Signed (Input Register)';
    }
    case ModbusDataType.Uint16: {
      return writable ? '16-bit Integer, Unsigned (Holding Register)' : '16-bit Integer, Unsigned (Input Register)';
    }
    case ModbusDataType.Uint32: {
      return writable ? '32-bit Integer, Unsigned (Holding Register)' : '32-bit Integer, Unsigned (Input Register)';
    }
    case ModbusDataType.Uint64: {
      return writable ? '64-bit Integer, Unsigned (Holding Register)' : '64-bit Integer, Unsigned (Input Register)';
    }
    default: {
      throw new Error('unsupported Modbus data type');
    }
  }
}


export function getFriendlyEndiannessString(endianness: ModbusEndianness): string {
  switch (endianness) {
    case ModbusEndianness.Empty: {
      return `${endianness} (n/a)`;
    }
    case ModbusEndianness.BigEndian: {
      return `${endianness} (Big-Endian)`;
    }
    case ModbusEndianness.LittleEndian: {
      return `${endianness} (Little-Endian)`;
    }
    case ModbusEndianness.BigEndian32: {
      return `${endianness} (Big-Endian, Normal)`;
    }
    case ModbusEndianness.LittleEndian32: {
      return `${endianness} (Little-Endian, Normal)`;
    }
    case ModbusEndianness.BigEndian32Swapped: {
      return `${endianness} (Big-Endian, Swapped)`;
    }
    case ModbusEndianness.LittleEndian32Swapped: {
      return `${endianness} (Little-Endian, Swapped)`;
    }
    case ModbusEndianness.BigEndian64: {
      return `${endianness} (Big-Endian, Normal)`;
    }
    case ModbusEndianness.LittleEndian64: {
      return `${endianness} (Little-Endian, Normal)`;
    }
    case ModbusEndianness.BigEndian64Swapped: {
      return `${endianness} (Big-Endian, Swapped)`;
    }
    case ModbusEndianness.LittleEndian64Swapped: {
      return `${endianness} (Little-Endian, Swapped)`;
    }
    default: {
      throw new Error('unsupported Modbus endianness value');
    }
  }
}


/**
 * Checks if a data type is a 1-bit boolean type.
 * 1-bit types take boolean values (true/false) rather than numeric values.
 */
export function is1BitDataType(dataType: ModbusDataType): boolean {
  return dataType === ModbusDataType.Bool;
}

//
// export function getFriendlyFunctionStrings(dataType: ModbusDataType, writable: boolean): ModbusFunctionType[] {
//   const funcs: ModbusFunctionType[] = [];
//
//   if (writable) {
//     switch (dataType) {
//       case 'int16': {
//         funcs.push(MODBUS_FUNC_READ_INPUT_REGISTERS);
//         break;
//       }
//       default: {
//         throw new Error('unsupported Modbus data type');
//       }
//     }
//   } else {
//     switch (dataType) {
//       case 'int16': {
//         funcs.push(MODBUS_FUNC_READ_MULTIPLE_HOLDING_REGISTERS, MODBUS_FUNC_WRITE_SINGLE_HOLDING_REGISTER);
//         break;
//       }
//       default: {
//         throw new Error('unsupported Modbus data type');
//       }
//     }
//   }
//
//   return funcs;
// }

export function makeTileFromModbusPoint(
  point: ModbusPoint,
  type: TileType,
  deviceId: DeviceId,
  icon: TileIcon = 'dot-circle',
): Tile {
  const configId: ModbusPointID = new ModbusPointID(point.ID);
  const tile = new Tile();

  tile.Name = point.Name;
  tile.Appearance = {
    Icon: icon,
    Color: '',
    Config: {
      Decimal: TILE_APPEARANCE_DEFAULT_DECIMAL,
      Increment: TILE_APPEARANCE_DEFAULT_INCREMENT,
      Multiplier: TILE_APPEARANCE_DEFAULT_MULTIPLIER,
    },
  };

  if (type) {
    tile.Type = type;
  } else {
    if ([ModbusDataType.Int16, ModbusDataType.Int32, ModbusDataType.Float32, ModbusDataType.Float64].indexOf(configId.Type) > -1) {
      tile.Type = TILE_TYPE_SIMPLE_ANALOG;
    } else if (configId.Type === ModbusDataType.Bool) {
      tile.Type = TILE_TYPE_SIMPLE_BOOL;
    }
  }

  let s: TileStructure;

  switch (tile.Type) {
    case TILE_TYPE_SIMPLE_BOOL:
      s = {
        Priority: 0,
        Protocol: DeviceType.Modbus,
        DeviceID: deviceId,
        PointID: configId.serialize(),
        DisplayFalse: '',
        DisplayTrue: '',
        Writable: configId.Writable,
        Conditions: [],
        AutoClear: '',
      };

      tile.Structure = s;
      break;
    case TILE_TYPE_SIMPLE_ANALOG:
    case TILE_TYPE_SIMPLE_MULTISTATE:
      let units: string = '';

      s = {
        Priority: 0,
        Protocol: DeviceType.Modbus,
        DeviceID: deviceId,
        PointID: configId.serialize(),
        DisplayRawUnits: units,
        DisplayStates: [],
        Writable: configId.Writable,
        Conditions: [],
        AutoClear: '',
      };

      tile.Structure = s;
      break;
    default:
      throw new Error('no tile type found');
  }

  tile.setDefaults();
  return tile;
}
